import React, { useCallback, useEffect, useState, useRef } from "react";
import BillingDetail from "../../components/Doctor/Dashboard/BillingDetail";
import MedicineDetail from "../../components/Doctor/Dashboard/MedicineDetail";
import PatientOrderDetail from "../../components/Doctor/Dashboard/PatientOrderDetail";
import QuickTrackingWidget from "../../components/Doctor/Dashboard/QuickTrackingWidget";
import UploadPrescription from "../../components/Doctor/Dashboard/uploadPrescription";
import ReceiptMaker from "../../components/Doctor/Dashboard/ReceiptMaker";
import PrescriptionMaker from "../../components/Doctor/Dashboard/PrescriptionMaker";
import QuickOrderOverlay from "../../components/Doctor/Dashboard/QuickOrderOverlay";
import Button from "../../components/common/Button";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { createOrder, createPdf } from "../../redux/Thunk/Order/OrderThunks";
import { clearLastCreatedPrescription } from "../../redux/Reducer/Prescription/PrescriptionSlice";
import { frequentProductList, fetchNRxDrugs } from "../../redux/Thunk/Product/ProductThunks";
import { createOrderValidation } from "../../components/common/Validation/createOrderValidation";
import toast from "react-hot-toast";
import apiAxios from "../../services/apiAxios";
import { setLoading } from "../../redux/Reducer/Order/OrderSlice";
import Loader from "../../components/Loader";
import { getDoctorDetails } from "../../redux/Thunk/Doctor/DoctorThunks";
import Complete from "../../components/common/icons/Complete";
import AIPrescriptionReader from "../../components/Doctor/Dashboard/AIPrescriptionReader";

const Dashboard = () => {
  const dispatch = useDispatch();
  const doctor = useSelector((state) => state?.doctor?.doctorDetails?.doctor);
  const frequentProducts = useSelector((state) => state?.product?.frequentProducts);
  const nrxDrugNames = useSelector((state) => state?.product?.nrxDrugNames || []);
  const doctorId = doctor?.DID || null;
  const SID = doctor?.SID || null;
  const is_pin_b = doctor?.is_pin_b;
  const autoSignature = doctor?.autoSignature;
  const receiptMakerAccess = doctor?.receiptMakerAccess;

  const loading = useSelector((state) => state?.order?.loading);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState("");
  const [cameraOn, setCameraOn] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [showLoader, setShowLoader] = useState(true);
  const [billingData, setBillingData] = useState(1);
  const [showQuickOrderOverlay, setShowQuickOrderOverlay] = useState(false);
  const prescriptionMakerRef = useRef(null);

  useEffect(() => {
    dispatch(fetchNRxDrugs());
    const id = doctorId || "";
    if (id?.length > 0) {
      let search = "";
      dispatch(frequentProductList({ id, productName: search }));
    }
  }, [doctorId, dispatch]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      dispatch(getDoctorDetails(token));
    }
  }, [dispatch]);

  const time = SID ? 100 : 2000;
  useEffect(() => {
    dispatch(setLoading(false));
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, time);

    return () => clearTimeout(timer);
  }, [dispatch, time]);

  // Capture healthmap patient context on first mount (useRef survives re-renders)
  const healthmapPatientRef = useRef(null);
  if (healthmapPatientRef.current === null) {
    const raw = sessionStorage.getItem("healthmap-patient-context");
    if (raw) {
      healthmapPatientRef.current = JSON.parse(raw);
      sessionStorage.removeItem("healthmap-patient-context");
    }
  }
  const tp = healthmapPatientRef.current;

  const initialValues = {
    patient: {
      phoneNumber: tp?.phoneNumber || "",
      name: tp?.name || "",
      surname: tp?.surname || "",
      gender: tp?.gender || "",
      dateofbirth: tp?.dateofbirth || "",
      age: tp?.age || "",
      pincode: tp?.pincode || "",
      city: tp?.city || "",
      PID: tp?.PID || null,
    },
    products: [
      {
        productId: "",
        productName: "",
        units: 0,
        rxDays: 0,
        orderQty: 0,
        rxUnits: "",
        orderUnits: 0,
        balanceUnits: "",
        dosageNote: "",
        mrp: "",
        discount: "",
        gst: "",
        netAmt: "",
        type: "",
        typeIndex: "",
        morningTime: 0,
        midDay: 0,
        eveningTime: 0,
        night: 0,
        takenTime: "A/F",
        takenTimeIndex: "",
        extra: 0,
        extraIndex: 0,
        drugs: [],
        qty: 0,
        uom: 0,
        lookupMargin: {
          type: ""
        }
      },
    ],
    returnProducts: [],
    filePath: "",
    isAIGenerated: false,
    billing: {
      subTotal: 0,
      totalDiscount: 0,
      handlingFee: 0,
      smallCartFee: 0,
      deliveryCharges: 0,
      gst: 0,
      payableAmount: 0,
    },
    delivery: {
      isAddress: false,
      isCollect: false,
      isClinic: true,
      address: {
        pincode: "",
        city: "",
        state: "",
        areaRoad: "",
        addressType: "",
        premisesName: "",
        premisesNoFloor: "",
        landmark: "",
        phoneNumber2: ""
      }
    },
    payment: "upi",
    DID: doctorId,
  };

  // useEffect(() => {
  //   if (!SID) {
  //     dispatch(getDoctorDetails(token));
  //   }
  // }, []);
  const updateFileValues = useCallback((selectedFile) => {
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setError(null);
    } else {
      setFileName("");
      setError("Please select a file first.");
    }
  }, []);

  const handleUpload = useCallback(
    async (selectedFile, folder = "") => {
      if (!selectedFile) {
        setError("Please select a file first.");
        return; // Exit if no file is selected
      }
      updateFileValues(selectedFile);
      dispatch(setLoading(true));
      setUploading(true);
      const formData = new FormData();
      formData.append("image", selectedFile);

      try {
        const url = folder ? `api/order/uploadImage?folder=${folder}` : "api/order/uploadImage";
        const response = await apiAxios.post(
          url,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        const filePath = response?.data?.filePath;

        if (filePath) {
          toast.success("File uploaded successfully!");
          setSelectedFile(filePath); // Assuming setSelectedFile is available to store the file path
          return filePath;
        } else {
          throw new Error("File path not returned from the API.");
        }
      } catch (err) {
        setError("Failed to upload file.");
        toast.error("Failed to upload file.");
        setSelectedFile("");
        setFile("");
        setFileName("");
        return null;
      } finally {
        setUploading(false);
        dispatch(setLoading(false));
      }
    },
    [dispatch, updateFileValues]
  );
  const checkValidation = (values) => {
    let errors = {};
    if (!autoSignature && !selectedFile.length) {
      if (cameraOn) {
        errors = `Please click and upload prescription photo!!`;
      } else {
        errors = `Please upload prescription!!`;
      }
    }
    // if (!values?.filePath) {
    //   errors = `Please select prescription image`;
    // }
    if (!values?.payment?.length) {
      errors = `Please Select payment type!!`;
    }

    if (values?.delivery?.isAddress) {
      const requiredFields = [
        "state",
        "pincode",
        "city",
        "areaRoad",
        "premisesName",
        "addressType",
        "premisesNoFloor",
      ];

      for (const field of requiredFields) {
        if (!values?.delivery?.address?.[field]) {
          if (field === "addressType") {
            errors = `Please select the ${field.replace(
              /([A-Z])/g,
              " $1"
            )} field.`;
          } else {
            errors = `Please fill out the ${field.replace(
              /([A-Z])/g,
              " $1"
            )} field.`;
          }
        }
      }
    }
    if (
      !values.delivery.isAddress &&
      !values.delivery.isCollect &&
      !values.delivery.isClinic
    ) {
      errors = `Please Select delivery address type!!`;
    }
    if (values?.products?.length > 0) {
      values.products.forEach((product, index) => {
        const isSpecialMode = product.isSOS || product.isNA || product.isReducingDose || product.isPeriodic || product.dosageMode === 'SOS' || product.dosageMode === 'NA' || product.dosageMode === 'REDUCING' || product.dosageMode === 'PERIODIC';
        const hasSlotDose = (product.morningTime || 0) > 0 || (product.midDay || 0) > 0 || (product.eveningTime || 0) > 0 || (product.night || 0) > 0;
        const isNaDuration = product.isNA || product.dosageMode === 'NA' || product.rxDaysDisplay === 'NA';

        if (!isSpecialMode && !hasSlotDose) {
          errors = `Please prescribe daily dosage for ${product.productName || 'medicine'} #${index + 1}!`;
        } else if (!isNaDuration && (!product.rxDays || product.rxDays <= 0 || isNaN(product.rxDays))) {
          errors = `Please enter RX days for ${product.productName || 'medicine'} #${index + 1}!`;
        }

        const qty = Number(product?.qty || 0);
        const orderQty = Number(product?.orderQty || 0);

        if (orderQty > qty && !product?.isCustom) {
          errors = `${product?.productName || ('medicine #' + (index + 1))} : required stock is not available, reduce the order quantity or add another product!`;
        }

        if (!product.productName) {
          errors = `Product is not selected for medicine #${index + 1}!`;
        }
      });
    } else {
      errors = "Please add at least one product!!";
    }

    return errors;
  };

  const checkValidationForPdf = (values) => {
    let errors = {};

    if (values?.patient?.phoneNumber?.length === 0) {
      errors = `Please Select Patient!!`;
    }

    if (values?.products?.length > 0) {
      values.products.forEach((product, index) => {
        const isSpecialMode = product.isSOS || product.isNA || product.isReducingDose || product.isPeriodic || product.dosageMode === 'SOS' || product.dosageMode === 'NA' || product.dosageMode === 'REDUCING' || product.dosageMode === 'PERIODIC';
        const hasSlotDose = (product.morningTime || 0) > 0 || (product.midDay || 0) > 0 || (product.eveningTime || 0) > 0 || (product.night || 0) > 0;
        const isNaDuration = product.isNA || product.dosageMode === 'NA' || product.rxDaysDisplay === 'NA';

        if (!product.productName) {
          errors = `Product name is required for medicine #${index + 1}!`;
        } else if (!isSpecialMode && !hasSlotDose) {
          errors = `Please prescribe daily dosage for ${product.productName || 'medicine'} #${index + 1}!`;
        } else if (!isNaDuration && (!product.rxDays || product.rxDays <= 0 || isNaN(product.rxDays))) {
          errors = `Please enter valid RX days for ${product.productName || 'medicine'} #${index + 1}!`;
        }
      });
    }

    return errors;
  };

  const checkIsNRxProduct = useCallback(
    (p) => {
      if (!p) return false;

      // 1. Direct product-level tags
      let rawTags = p.tags || p?.product?.tags || p?.product?.product?.tags || p?.Tag || p?.product?.Tag;
      if (!rawTags || (Array.isArray(rawTags) && rawTags.length === 0)) {
        const pIID = p.productId || p.IID || p.id;
        if (pIID && Array.isArray(frequentProducts)) {
          const found = frequentProducts.find((fp) => {
            const item = fp?.product || fp;
            return String(item?.IID || item?.id) === String(pIID);
          });
          if (found) {
            const foundItem = found.product || found;
            rawTags = foundItem.tags || foundItem.Tag;
          }
        }
      }
      if (rawTags) {
        if (typeof rawTags === "string") {
          try {
            rawTags = JSON.parse(rawTags);
          } catch (e) {
            rawTags = rawTags.split(",").map((s) => s.trim());
          }
        }
        if (!Array.isArray(rawTags)) {
          rawTags = [rawTags];
        }
        const hasProductNRx = rawTags.some((t) => {
          if (!t) return false;
          const name = typeof t === "string" ? t : t?.tagName || t?.name || t?.tag;
          const norm = name?.toString()?.trim()?.toUpperCase();
          return norm === "NRX/H1" || norm === "NRX";
        });
        if (hasProductNRx) return true;
      }

      // 2. Direct drugTags on product
      let rawDrugTags = p.drugTags || p?.product?.drugTags || p?.product?.product?.drugTags;
      if (Array.isArray(rawDrugTags) && rawDrugTags.length > 0) {
        const hasDrugNRxTag = rawDrugTags.some((t) => {
          const name = typeof t === "string" ? t : t?.tagName || t?.name || t?.tag;
          const norm = name?.toString()?.trim()?.toUpperCase();
          return norm === "NRX/H1" || norm === "NRX";
        });
        if (hasDrugNRxTag) return true;
      }

      // 3. Drug names matching nrxDrugNames list
      let rawDrugs = p.drugs || p?.product?.drugs || p?.product?.product?.drugs;
      if (!rawDrugs || (Array.isArray(rawDrugs) && rawDrugs.length === 0)) {
        const pIID = p.productId || p.IID || p.id;
        if (pIID && Array.isArray(frequentProducts)) {
          const found = frequentProducts.find((fp) => {
            const item = fp?.product || fp;
            return String(item?.IID || item?.id) === String(pIID);
          });
          if (found) {
            const foundItem = found.product || found;
            rawDrugs = foundItem.drugs;
          }
        }
      }
      if (Array.isArray(rawDrugs) && rawDrugs.length > 0 && Array.isArray(nrxDrugNames) && nrxDrugNames.length > 0) {
        const hasMatchingNRxDrug = rawDrugs.some((d) => {
          if (!d) return false;
          const dName = (typeof d === "string" ? d : d?.drugName || d?.name)?.toString()?.trim()?.toUpperCase();
          if (!dName) return false;
          return nrxDrugNames.some((nrx) => {
            const nrxNorm = nrx?.toString()?.trim()?.toUpperCase();
            return nrxNorm && (nrxNorm === dName || dName.includes(nrxNorm) || nrxNorm.includes(dName));
          });
        });
        if (hasMatchingNRxDrug) return true;
      }

      return false;
    },
    [frequentProducts, nrxDrugNames]
  );

  const handleSubmit = async (values, { resetForm }) => {
    // const filePath = selectedFile;
    if (selectedFile?.length > 0) {
      values.filePath = selectedFile;
    }

    const hasSingleNRxProductBlock =
      values?.products?.length === 1 && checkIsNRxProduct(values?.products[0]);

    if (hasSingleNRxProductBlock) {
      toast.error("Orders cannot be placed for a prescription containing only a single NRX/H1 product.");
      return;
    }

    if (values?.hasIneligibleReturnProduct) {
      toast.error("Cannot submit order: One or more return products were not ordered by this patient in the last 30 days.");
      return;
    }

    const errors = checkValidation(values);

    if (Object.keys(errors).length > 0) {
      toast.error(errors);
      return;
    }

    // ─── Smart XID Logic ───
    // Get XID from PrescriptionMaker ref (handles reuse / auto-create / confirmation)
    let orderXID = null;
    if (values.isAIGenerated) {
      // Do not get or generate XID, use filePath directly which is already set
    } else if (prescriptionMakerRef.current?.getXIDForOrder) {
      const xidResult = await prescriptionMakerRef.current.getXIDForOrder();
      if (xidResult === null) {
        // User cancelled the confirmation dialog
        return;
      }
      orderXID = xidResult || null;
    }

    if (values?.patient?.dateofbirth && String(values.patient.dateofbirth).trim().length > 0) {
      const date = new Date(values.patient.dateofbirth);
      const formattedDate = date.toLocaleDateString('en-CA');
      values.patient.dateofbirth = formattedDate;
    } else {
      values.patient.dateofbirth = null;
    }
    const clonedValues = JSON.parse(JSON.stringify(values));
    // Recursive function to remove specific keys
    const removeSpecificKeys = (obj) => {
      if (Array.isArray(obj)) {
        obj.forEach((item) => removeSpecificKeys(item));
      } else if (typeof obj === "object" && obj !== null) {
        Object.keys(obj).forEach((key) => {
          if (["typeIndex", "takenTimeIndex"].includes(key)) {
            delete obj[key]; // Remove the specific keys
          } else if (typeof obj[key] === "object") {
            removeSpecificKeys(obj[key]); // Recursively handle nested objects
          }
        });
      }
      if (obj?.delivery?.isClinic || obj?.delivery?.isCollect) {
        delete obj.delivery.address;
      }
    };

    const regularProducts = (clonedValues?.products || []).filter(
      (product) => product?.productName?.length > 0
    );
    const returnProds = (clonedValues?.returnProducts || [])
      .filter((product) => product?.productName?.length > 0)
      .map((product) => ({ ...product, isReturn: true }));
    clonedValues.products = [...regularProducts, ...returnProds];

    // Remove the specific keys
    removeSpecificKeys(clonedValues);

    // Attach XID to order if available
    if (orderXID) {
      clonedValues.XID = orderXID;
    }

    // Attach source
    clonedValues.source = clonedValues.isAIGenerated ? 'scan' : 'standard';

    try {
      const response = await dispatch(createOrder(clonedValues)).unwrap();
      if (response?.status === 200 || response?.status === 201) {
        const savedPID = response?.data?.message?.PID;

        setFileName("");
        setFile("");

        // Reset form but preserve patient info (including new PID)
        resetForm({
          values: {
            ...initialValues,
            patient: {
              ...values.patient,
              PID: savedPID || values.patient.PID
            }
          }
        });

        setCameraOn(false);
        setImageData("");
        setSelectedFile("");
        // Clear prescription state after successful order
        dispatch(clearLastCreatedPrescription());
        if (prescriptionMakerRef.current?.resetAfterOrder) {
          prescriptionMakerRef.current.resetAfterOrder();
        }
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("Complete Error Object:", error);

      if (error.response) {
        if (error) {
          console.error("API Error:", error.error);
        } else {
          console.error("Unexpected Error:", error);
        }
      }
    }
  };
  const handlePrintPdf = async (values, setFieldValue) => {
    const errors = checkValidationForPdf(values);

    if (Object.keys(errors).length > 0) {
      toast.error(errors);
      return;
    }

    const response = await dispatch(createPdf(values)).unwrap();
    if (response?.status === 200) {
      if (response?.data?.success && response?.data?.url) {
        // Update Formik PID if returned
        if (response.data.patient?.PID && setFieldValue) {
          setFieldValue("patient.PID", response.data.patient.PID);
        }

        const res = await fetch(response.data.url);
        values.pdfPath = response?.data?.url;
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = values?.patient?.name
          ? `${values?.patient?.name} ${values?.patient?.surname}_prescription.pdf`
          : `generated_file.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
        toast.success("Pdf successfully created.");
      }
    } else {
      toast.error(response?.message);
    }
  };
  const [isPhoneLocked, setIsPhoneLocked] = useState(false);
  const [activeRxTab, setActiveRxTab] = useState("create");

  const handleRxTabChange = (tab) => {
    setActiveRxTab(tab);
    // If we switch to create, unlock phone. If switch to open/edit, lock if prescription is already there
    if (tab === "create") {
      setIsPhoneLocked(false);
    } else {
      const currentRx = prescriptionMakerRef.current?.getCurrentPrescription?.() || null;
      setIsPhoneLocked(!!currentRx);
    }
  };

  const handleRxLoaded = (rx) => {
    if (activeRxTab !== "create") {
      setIsPhoneLocked(true);
    }
  };

  return (
    <>
      {showLoader ? (
        <Loader loading={showLoader} />
      ) : SID ? (
        <Formik
          initialValues={initialValues}
          validationSchema={createOrderValidation}
          onSubmit={handleSubmit}
        >
          {({
            values,
            handleChange,
            setFieldValue,
            setValues,
            touched,
            errors,
            setFieldTouched,
            handleBlur,
          }) => (
            <Form className="dashboard-container">
              <Loader loading={loading} />
              <div className="flex gap-[8px] mb-2 flex-wrap flex-col w-full">
                <QuickTrackingWidget
                  rapidScanSlot={
                    doctor?.rapidScanAccess !== false && (
                      <AIPrescriptionReader
                        handleUpload={handleUpload}
                        setFieldValue={setFieldValue}
                        setValues={setValues}
                        values={values}
                        setBillingData={setBillingData}
                        onSuccessSwitchTab={(tab) => {
                          if (prescriptionMakerRef.current?.setActiveTab) {
                            prescriptionMakerRef.current.setActiveTab(tab);
                          }
                        }}
                      />
                    )
                  }
                  isAIGenerated={doctor?.rapidScanAccess !== false && values.isAIGenerated}
                />

                <PatientOrderDetail
                  values={values}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  touched={touched}
                  errors={errors}
                  setFieldTouched={setFieldTouched}
                  handleBlur={handleBlur}
                  readOnlyPhone={isPhoneLocked}
                  onQuickOrder={() => setShowQuickOrderOverlay(true)}
                />
              </div>

              {/* Quick Order Overlay */}
              <QuickOrderOverlay
                isOpen={showQuickOrderOverlay}
                onClose={() => setShowQuickOrderOverlay(false)}
                patient={values.patient}
              />
              {/* Prescription Maker */}
              <PrescriptionMaker
                ref={prescriptionMakerRef}
                values={values}
                setFieldValue={setFieldValue}
                handleSubmit={handleSubmit}
                selectedFile={selectedFile}
                autoSignature={autoSignature}
                is_pin_b={is_pin_b}
                rxCreateAccess={doctor?.rxCreateAccess !== false}
                rxEditAccess={doctor?.rxEditAccess !== false}
                rxListAccess={doctor?.rxListAccess !== false}
                generateRxCardAccess={doctor?.generateRxCardAccess !== false}
                rxHealthRecordAccess={doctor?.rxHealthRecordAccess !== false}
                generateOrderAccess={doctor?.generateOrderAccess !== false}
                handleUpload={handleUpload}
                file={file}
                setBillingData={setBillingData}
                onTabChange={handleRxTabChange}
                onPrescriptionLoaded={handleRxLoaded}
                medicineDetailSlot={
                  <MedicineDetail
                    setFieldValue={setFieldValue}
                    values={values}
                    handleChange={handleChange}
                    billingData={billingData}
                    setBillingData={setBillingData}
                  />
                }
                billingDetailSlot={
                  <BillingDetail
                    setFieldValue={setFieldValue}
                    values={values}
                    handleChange={handleChange}
                    billingData={billingData}
                    setBillingData={setBillingData}
                  />
                }
                uploadPrescriptionSlot={
                  <UploadPrescription
                    values={values}
                    file={file}
                    setFile={setFile}
                    uploading={uploading}
                    setUploading={setUploading}
                    error={error}
                    setError={setError}
                    handleUpload={handleUpload}
                    fileName={fileName}
                    setFileName={setFileName}
                    selectedFile={selectedFile}
                    setSelectedFile={setSelectedFile}
                    handlePrintPdf={handlePrintPdf}
                    cameraOn={cameraOn}
                    setCameraOn={setCameraOn}
                    imageData={imageData}
                    setImageData={setImageData}
                    autoSignature={autoSignature}
                  />
                }
                submitOrderSlot={
                  <>
                    {(() => {
                      const isPatientDetailsFilled =
                        values?.patient?.phoneNumber?.length > 0 &&
                        values?.patient?.name?.trim()?.length > 0 &&
                        values?.patient?.surname?.trim()?.length > 0 &&
                        values?.patient?.gender?.length > 0 &&
                        (String(values?.patient?.dateofbirth)?.length > 0 || String(values?.patient?.age)?.trim()?.length > 0) &&
                        (String(values?.patient?.pincode)?.length === 6 || String(values?.patient?.city)?.trim()?.length > 0);
                      const isPaymentSelected = values?.payment?.length > 0;
                      const isDeliverySelected =
                        values?.delivery?.isAddress ||
                        values?.delivery?.isCollect ||
                        values?.delivery?.isClinic;
                      const isAddressFieldsFilled = !values?.delivery?.isAddress || (
                        values?.delivery?.address?.state?.length > 0 &&
                        values?.delivery?.address?.pincode?.length > 0 &&
                        values?.delivery?.address?.city?.length > 0 &&
                        values?.delivery?.address?.areaRoad?.length > 0 &&
                        values?.delivery?.address?.premisesName?.length > 0 &&
                        values?.delivery?.address?.addressType?.length > 0 &&
                        values?.delivery?.address?.premisesNoFloor?.length > 0
                      );
                      const allProductsDataFilled = values?.products?.length > 0 && values?.products?.every((product) => {
                        const currentMode = product?.dosageMode || (product?.isSOS ? "SOS" : product?.isNA ? "NA" : product?.isReducingDose ? "REDUCING" : product?.isPeriodic ? "PERIODIC" : "REGULAR");
                        const hasProductName = Boolean(product?.productName && product?.productName?.trim() !== "");

                        const hasValidDosage =
                          currentMode === 'SOS' ||
                          currentMode === 'NA' ||
                          (currentMode === 'REDUCING' && Array.isArray(product?.reducingDoses) && product.reducingDoses.length > 0) ||
                          (currentMode === 'PERIODIC' && (product?.morningTime > 0 || product?.midDay > 0 || product?.eveningTime > 0 || product?.night > 0 || product?.periodicFrequency)) ||
                          (currentMode === 'REGULAR' && (product?.morningTime > 0 || product?.midDay > 0 || product?.eveningTime > 0 || product?.night > 0));

                        const hasValidDuration = currentMode === 'NA' || currentMode === 'SOS' || (Number(product?.rxDays) > 0 || product?.rxDaysDisplay === 'NA');

                        const isCustom = product?.isCustom === true || (product?.productId && String(product?.productId).startsWith("CP")) || (product?.IID && String(product?.IID).startsWith("CP"));
                        const hasValidQuantity = Number(product?.orderQty) > 0 || isCustom;

                        return hasProductName && hasValidDosage && hasValidDuration && hasValidQuantity;
                      });
                      const isPrescriptionUploaded = autoSignature || selectedFile?.length > 0;
                      const hasSingleNRxProductBlock =
                        values?.products?.length === 1 && checkIsNRxProduct(values?.products[0]);
                      const isAllDataFilled =
                        isPatientDetailsFilled &&
                        isPaymentSelected &&
                        isDeliverySelected &&
                        isAddressFieldsFilled &&
                        allProductsDataFilled &&
                        isPrescriptionUploaded &&
                        !values?.hasIneligibleReturnProduct &&
                        !hasSingleNRxProductBlock;
                      let buttonText = "Submit Order";
                      const buttonClass = isAllDataFilled
                        ? "flex text-[13px] items-center justify-center gap-2 text-white bg-success-600 w-full p-2 rounded-[8px] hover:text-secondry-600 hover:bg-success-900 hover:border-success-600 focus:border-success-200 border-[2px] border-success-600"
                        : "flex text-[13px] items-center justify-center gap-2 text-white bg-primary-600 w-full p-2 rounded-[8px] hover:bg-primary-700 hover:border-primary-800 focus:border-primary-200 border-[2px] border-primary-600";
                      return (
                        <>
                          {hasSingleNRxProductBlock && (
                            <div className="mb-2 p-2 bg-amber-50 border border-amber-300 rounded-[6px] text-amber-800 text-[11px] font-medium text-center leading-snug">
                              ⚠ Order creation blocked: Prescriptions containing only a single <strong className="font-bold">NRX/H1</strong> product cannot be submitted as an order. Please add atleast one subsidery product to place the order.
                            </div>
                          )}
                          <span className="text-primary-400 font-light text-[10px] mb-1 block text-center">
                            * With patient consent, prescriptions are shared with third-party pharmacies to process medication orders. Check our{" "}
                            <a
                              href="/terms"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-500 underline hover:text-primary-700 font-regular"
                            >
                              Terms of Service
                            </a>{" "}
                            for more!
                          </span>
                          <Button
                            type="submit"
                            disabled={!isAllDataFilled}
                            className={buttonClass}
                          >
                            <Complete fill="currentColor" width={20} height={20} />
                            {buttonText}
                          </Button>
                        </>
                      );
                    })()}
                  </>
                }
              />
              {/* Receipt Maker */}
              {receiptMakerAccess && (
                <ReceiptMaker
                  values={values}
                  setFieldValue={setFieldValue}
                  is_pin_b={is_pin_b}
                  rcptServicesAccess={doctor?.rcptServicesAccess !== false}
                  rcptListAccess={doctor?.rcptListAccess !== false}
                />
              )}
              {/* {pdfUrl && (
                <iframe
                  src={pdfUrl}
                  width="100%"
                  height="600px"
                  style={{ border: "none" }}
                />
              )} */}
            </Form>
          )}
        </Formik>
      ) : (
        <div className="min-h-[calc(100vh_-_80px)] flex items-center justify-center py-4 pr-4 w-full">
          <h2 className="text-center">
            You are not connected with any store!!
          </h2>
        </div>
      )}
    </>
  );
};

export default Dashboard;
